// 定义事件处理器类型
type EventHandler = (data: any) => void;

/**
 * 事件总线类
 * 实现发布-订阅模式，用于组件间通信
 */
export class EventBus {
  private static instance: EventBus;
  private handlers: Map<string, EventHandler[]>;

  // 私有构造函数，确保单例模式
  private constructor() {
    this.handlers = new Map<string, EventHandler[]>();
  }

  // 获取单例实例
  public static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * 订阅事件
   * @param eventName 事件名称
   * @param handler 事件处理函数
   * @returns 取消订阅的函数
   */
  public subscribe(eventName: string, handler: EventHandler): () => void {
    // 如果事件不存在，创建新的处理器数组
    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, []);
    }

    // 添加处理器
    const handlers = this.handlers.get(eventName)!;
    handlers.push(handler);

    // 返回取消订阅的函数
    return () => {
      this.unsubscribe(eventName, handler);
    };
  }

  /**
   * 取消订阅事件
   * @param eventName 事件名称
   * @param handler 事件处理函数
   */
  public unsubscribe(eventName: string, handler: EventHandler): void {
    if (!this.handlers.has(eventName)) return;

    const handlers = this.handlers.get(eventName)!;
    const index = handlers.indexOf(handler);

    if (index !== -1) {
      handlers.splice(index, 1);
    }

    // 如果没有处理器了，删除事件
    if (handlers.length === 0) {
      this.handlers.delete(eventName);
    }
  }

  /**
   * 发布事件
   * @param eventName 事件名称
   * @param data 事件数据
   */
  public publish(eventName: string, data?: any): void {
    if (!this.handlers.has(eventName)) return;

    const handlers = this.handlers.get(eventName)!;
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${eventName}:`, error);
      }
    });
  }

  /**
   * 清除所有事件订阅
   */
  public clear(): void {
    this.handlers.clear();
  }
}

// 导出单例实例
export const eventBus = EventBus.getInstance();

// 默认导出
export default eventBus;

// 定义标准事件类型
export enum AIEventType {
  SHOW_RESPONSE = 'ai:showResponse',
  HIDE_RESPONSE = 'ai:hideResponse',
  LOADING = 'ai:loading',
  ERROR = 'ai:error',
  CLEAR = 'ai:clear',
  FULLSCREEN = 'ai:fullscreen',
  MINIMIZE = 'ai:minimize',
  // 新增事件类型
  STREAM_START = 'ai:streamStart',
  STREAM_CHUNK = 'ai:streamChunk',
  STREAM_END = 'ai:streamEnd',
  WORKFLOW_PROGRESS = 'ai:workflowProgress',
  PROCESSING_STAGE = 'ai:processingStage'
}

// 定义响应数据接口
export interface AIResponseData {
  id?: string;
  type: string; // 响应类型: 'text', 'chart', 'table', 'image', 等
  content: any; // 响应内容
  metadata?: Record<string, any>; // 额外元数据
  sessionId?: string; // 会话ID
}

// 定义流式响应数据接口
export interface AIStreamData extends AIResponseData {
  sessionId: string; // 会话ID，流式响应必须包含
  isComplete?: boolean; // 是否是完整响应
  chunk?: string; // 当前文本块
}

// 定义工作流进度数据接口
export interface AIWorkflowProgressData {
  sessionId: string; // 会话ID
  workflowRunId: string; // 工作流运行ID
  status: 'running' | 'succeeded' | 'failed' | 'stopped'; // 工作流状态
  totalSteps?: number; // 总步骤数
  elapsedTime?: number; // 已用时间
  error?: string; // 错误信息
}

// 定义处理阶段数据接口
export interface AIProcessingStageData {
  sessionId: string; // 会话ID
  stage: string; // 处理阶段
  nodeTitle?: string; // 节点标题
} 